import React, { useEffect, useMemo, useRef } from "react";
import { Formik, Form, FormikHelpers, useFormikContext } from "formik";
import * as Yup from "yup";
import { useMediaQuery } from "@mui/material";

import { ContactValues } from "../../types/RandomGlobalTypes";
import { useI18n } from "../../languages/providers/I18nProvider";
import { useTheme } from "../../providers/ThemeContext";
import { ContactFormStyles } from "./ContactFormStyles";
import InputField from "../common/InputField";
import { contactFormKeys } from "./contactFormContent";
import { contactService } from "../../services/ContactService";

const initialValues: ContactValues = {
  fullName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

// Inner component so we can access Formik context
function LangSyncWatcher() {
  const { lang } = useI18n();
  const { touched, validateForm, setErrors } = useFormikContext<ContactValues>();
  const prevLang = useRef(lang);

  useEffect(() => {
    if (prevLang.current === lang) return;
    prevLang.current = lang;

    // Only re-validate if the user has already tried to submit (fields are touched)
    const hasTouched = Object.values(touched).some(Boolean);
    if (hasTouched) {
      // Clear stale errors first, then re-validate with new schema's messages
      setErrors({});
      setTimeout(() => validateForm(), 0);
    }
  }, [lang, touched, validateForm, setErrors]);

  return null;
}

export default function ContactForm() {
  const { theme } = useTheme();
  const { t, lang } = useI18n();

  const isMobile = useMediaQuery("(max-width: 1100px)");
  const s = ContactFormStyles(theme, isMobile);

  // Schema rebuilds when lang changes (t changes)
  const schema = useMemo(
    () =>
      Yup.object({
        fullName: Yup.string()
          .trim()
          .min(2, t(contactFormKeys.fields.fullName.errors.min))
          .max(60, t(contactFormKeys.fields.fullName.errors.max))
          .required(t(contactFormKeys.fields.fullName.errors.required)),
        email: Yup.string()
          .trim()
          .email(t(contactFormKeys.fields.email.errors.email))
          .required(t(contactFormKeys.fields.email.errors.required)),
        phone: Yup.string()
          .trim()
          .max(30, t(contactFormKeys.fields.phone.errors.max)),
        subject: Yup.string()
          .trim()
          .min(3, t(contactFormKeys.fields.subject.errors.min))
          .max(100, t(contactFormKeys.fields.subject.errors.max))
          .required(t(contactFormKeys.fields.subject.errors.required)),
        message: Yup.string()
          .trim()
          .min(5, t(contactFormKeys.fields.message.errors.min))
          .max(2000, t(contactFormKeys.fields.message.errors.max))
          .required(t(contactFormKeys.fields.message.errors.required)),
      }),
    [t]
  );

  async function handleSubmit(
    values: ContactValues,
    helpers: FormikHelpers<ContactValues>
  ) {
    await contactService.contact(values);
    helpers.setSubmitting(false);
    helpers.resetForm();
  }

  return (
    <div style={s.box}>
      <p style={s.title}>{t(contactFormKeys.hero.title)}</p>

      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, setTouched }) => {
          const touchAll = () => {
            const allTouched = Object.keys(initialValues).reduce(
              (acc, key) => ({ ...acc, [key]: true }),
              {} as Record<keyof ContactValues, boolean>
            );
            setTouched(allTouched, true);
          };

          return (
            <Form noValidate>
              {/* Watches lang changes and re-validates if errors are visible */}
              <LangSyncWatcher />

              <div style={s.grid(isMobile ? 1 : 2)}>
                <div style={s.cell}>
                  <InputField
                    name="fullName"
                    label={t(contactFormKeys.fields.fullName.label)}
                    placeholder={t(contactFormKeys.fields.fullName.placeholder)}
                    required
                  />
                </div>
                <div style={s.cell}>
                  <InputField
                    name="email"
                    label={t(contactFormKeys.fields.email.label)}
                    type="email"
                    placeholder={t(contactFormKeys.fields.email.placeholder)}
                    required
                  />
                </div>
                <div style={s.cell}>
                  <InputField
                    name="phone"
                    label={t(contactFormKeys.fields.phone.label)}
                    type="tel"
                    placeholder={t(contactFormKeys.fields.phone.placeholder)}
                  />
                </div>
                <div style={s.cell}>
                  <InputField
                    name="subject"
                    label={t(contactFormKeys.fields.subject.label)}
                    placeholder={t(contactFormKeys.fields.subject.placeholder)}
                    required
                  />
                </div>
                <div style={s.fullRow}>
                  <InputField
                    name="message"
                    label={t(contactFormKeys.fields.message.label)}
                    type="textarea"
                    placeholder={t(contactFormKeys.fields.message.placeholder)}
                    required
                    rows={6}
                  />
                </div>
              </div>

              <div style={s.btnRow}>
                <button
                  type="submit"
                  onClick={touchAll}
                  disabled={isSubmitting}
                  style={s.btn}
                >
                  {isSubmitting
                    ? t(contactFormKeys.buttons.submitting)
                    : t(contactFormKeys.buttons.submit)}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}